// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

import './raf-shim';
import './styles/main.css';
import { registerSW } from 'virtual:pwa-register';
import { createUI } from './ui';
import type { UI } from './ui';
import type { ExportSettings } from './types';
import { loadPdf, renderPageToCanvas, findBoxesOnPage, MAX_FILE_BYTES, MAX_PAGES, type LoadedPdf } from './pdf';
import { deriveOutName, looksLikePdf, clamp } from './redact';
import { exportRedacted } from './exporter';
import { formatBytes } from './format';
import { emit } from './eventlog';

const SAMPLE_URL = `${import.meta.env.BASE_URL}samples/sample-confidential.pdf`;
const STORE_KEY = 'redactwell.settings.v1';

const root = document.getElementById('app')!;
let ui: UI;
let current: LoadedPdf | null = null;
let currentName = 'document.pdf';
let lastBlob: Blob | null = null;
let busyLock = false;

function main(): void {
  ui = createUI(root, {
    onFile: handleFile,
    onSample: handleSample,
    onReset: reset,
    onFind: handleFind,
    onExport: runExport,
    onShare: share,
    onBoxesChanged: saveSettings,
  });

  ui.setExportSettings(loadSettings());
  ui.showStage('empty');
  emit('system', 'ok', 'Redactwell ready — drop a PDF to begin.');
  registerSW({ immediate: true });
}

// ── File ingestion ───────────────────────────────────────────────────────
async function handleFile(file: File): Promise<void> {
  if (busyLock) return;
  const name = file.name || 'document.pdf';
  if (file.size > MAX_FILE_BYTES) {
    ui.setError(`"${name}" is larger than ${formatBytes(MAX_FILE_BYTES)} — please pick a smaller PDF.`);
    emit('input', 'err', 'Rejected oversized file', { bytes: file.size });
    return;
  }

  ui.clearError();
  busyLock = true;
  ui.showStage('working');
  ui.setFileName(name);
  ui.setBusy(true, 'Opening PDF…');
  emit('input', 'info', 'Opening PDF…', { name, size: formatBytes(file.size) });

  try {
    const bytes = await file.arrayBuffer();
    if (!looksLikePdf(bytes)) throw new Error(`"${name}" doesn't look like a PDF file.`);

    destroyCurrent();
    const loaded = await loadPdf(bytes);
    current = loaded;
    currentName = name;
    lastBlob = null;
    ui.setShareVisible(false);

    ui.setDocument(loaded.pages, (index, canvas) => renderDisplayPage(index, canvas));
    ui.setBusy(false);

    if (loaded.doc.numPages > MAX_PAGES) {
      ui.setError(`This PDF has ${loaded.doc.numPages} pages — showing the first ${MAX_PAGES}.`);
      emit('input', 'warn', 'Page cap reached', { pages: loaded.doc.numPages, cap: MAX_PAGES });
    }
    emit('input', 'ok', 'PDF opened', { pages: loaded.pages.length });
  } catch (err) {
    ui.setBusy(false);
    const msg = errorMessage(err, name);
    ui.setError(msg);
    ui.showStage('empty');
    emit('input', 'err', 'Could not open PDF', { error: String(err) });
  } finally {
    busyLock = false;
  }
}

function errorMessage(err: unknown, name: string): string {
  const e = err as { name?: string; message?: string };
  if (e?.name === 'PasswordException') {
    return `"${name}" is password-protected. Remove the password in your PDF viewer, then try again.`;
  }
  if (e?.name === 'InvalidPDFException') {
    return `"${name}" appears to be damaged or is not a valid PDF.`;
  }
  return e?.message || 'Could not open that PDF.';
}

async function handleSample(): Promise<void> {
  if (busyLock) return;
  try {
    emit('input', 'info', 'Loading sample document…');
    const res = await fetch(SAMPLE_URL);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    handleFile(new File([blob], 'sample-confidential.pdf', { type: 'application/pdf' }));
  } catch (err) {
    ui.setError('Could not load the sample. Try choosing a PDF instead.');
    emit('input', 'err', 'Sample load failed', { error: String(err) });
  }
}

/** Render a page for on-screen display, sized to the current column width. */
async function renderDisplayPage(index: number, canvas: HTMLCanvasElement): Promise<void> {
  if (!current) return;
  const info = current.pages[index];
  const columnCss = Math.max(280, canvas.parentElement?.clientWidth || 820);
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const scale = clamp((columnCss * dpr) / info.width, 1, 3);
  await renderPageToCanvas(current.doc, index, scale, canvas);
}

// ── Find & redact ────────────────────────────────────────────────────────
async function handleFind(term: string): Promise<void> {
  if (!current || busyLock) return;
  busyLock = true;
  ui.setBusy(true, `Searching for “${term}”…`);
  emit('redact', 'info', 'Find & redact', { term });
  let total = 0;
  let firstPage = -1;
  try {
    for (const p of current.pages) {
      const boxes = await findBoxesOnPage(current.doc, p.index, term);
      if (boxes.length) {
        const added = ui.addBoxesToPage(p.index, boxes);
        total += added;
        if (firstPage < 0 && added > 0) firstPage = p.index;
      }
      await new Promise((r) => setTimeout(r, 0));
    }
    ui.setBusy(false);
    if (total > 0) {
      ui.toast(`Placed ${total} box${total === 1 ? '' : 'es'} — review, then export`);
      if (firstPage >= 0) ui.scrollToPage(firstPage);
      emit('redact', 'ok', 'Matches boxed', { term, boxes: total });
    } else {
      ui.toast(`No matches for “${term}” in the text layer`);
      emit('redact', 'warn', 'No matches', { term });
    }
  } catch (err) {
    ui.setBusy(false);
    ui.setError('Search failed on this document.');
    emit('redact', 'err', 'Find failed', { error: String(err) });
  } finally {
    busyLock = false;
  }
}

// ── Export ───────────────────────────────────────────────────────────────
async function runExport(): Promise<void> {
  if (!current || busyLock) return;
  const settings = ui.getExportSettings();
  const boxes = ui.getBoxes();
  if (ui.totalBoxes() === 0 && !settings.flattenAll) {
    ui.toast('Drag a box over something to redact first');
    return;
  }

  busyLock = true;
  lastBlob = null;
  ui.setShareVisible(false);
  ui.clearError();
  ui.setBusy(true, 'Preparing…', 2);
  emit('export', 'info', 'Exporting redacted PDF', {
    boxes: ui.totalBoxes(),
    dpi: settings.dpi,
    flattenAll: String(settings.flattenAll),
  });

  try {
    const res = await exportRedacted(
      current.doc,
      current.bytes,
      current.pages,
      boxes,
      settings,
      (phase, pct) => ui.setBusy(true, phase, pct),
    );
    ui.setBusy(false);
    lastBlob = res.blob;
    await deliver(res.blob);
    ui.setShareVisible(shareSupported());
    emit('export', 'ok', 'Redacted PDF ready', {
      size: formatBytes(res.blob.size),
      flattened: res.flattenedPages,
    });
  } catch (err) {
    ui.setBusy(false);
    ui.setError(err instanceof Error ? err.message : 'Could not build the redacted PDF.');
    emit('export', 'err', 'Export failed', { error: String(err) });
  } finally {
    busyLock = false;
  }
}

async function deliver(blob: Blob): Promise<void> {
  const name = deriveOutName(currentName);
  const fsa = (window as unknown as { showSaveFilePicker?: unknown }).showSaveFilePicker;
  if (typeof fsa === 'function') {
    try {
      const handle = await (
        fsa as (o: unknown) => Promise<{
          createWritable: () => Promise<{
            write: (d: Blob) => Promise<void>;
            close: () => Promise<void>;
          }>;
        }>
      )({
        suggestedName: name,
        types: [{ description: 'PDF document', accept: { 'application/pdf': ['.pdf'] } }],
      });
      const w = await handle.createWritable();
      await w.write(blob);
      await w.close();
      ui.toast('Saved redacted PDF');
      emit('export', 'ok', 'Saved', { name });
      return;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return; // user cancelled
      // fall through to anchor download
    }
  }
  anchorDownload(blob, name);
  ui.toast('Downloaded redacted PDF');
  emit('export', 'ok', 'Downloaded', { name });
}

function anchorDownload(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function shareSupported(): boolean {
  const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
  if (!nav.share || !nav.canShare) return false;
  try {
    const probe = new File([new Uint8Array([37, 80, 68, 70])], 'probe.pdf', {
      type: 'application/pdf',
    });
    return nav.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

async function share(): Promise<void> {
  if (!lastBlob) return;
  const file = new File([lastBlob], deriveOutName(currentName), { type: 'application/pdf' });
  try {
    await (navigator as Navigator).share({ files: [file], title: 'Redacted PDF' });
    emit('export', 'ok', 'Shared redacted PDF');
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') return;
    ui.setError('Sharing failed on this device. Use Download instead.');
  }
}

// ── Reset & persistence ────────────────────────────────────────────────
function destroyCurrent(): void {
  if (current) {
    current.doc.destroy().catch(() => {});
    current = null;
  }
}

function reset(): void {
  if (busyLock) return;
  destroyCurrent();
  lastBlob = null;
  currentName = 'document.pdf';
  ui.setShareVisible(false);
  ui.clearError();
  ui.setBusy(false);
  ui.showStage('empty');
  emit('ui', 'info', 'Cleared — ready for a new PDF.');
}

function loadSettings(): ExportSettings {
  const fallback: ExportSettings = { dpi: 150, sharp: false, flattenAll: false };
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return fallback;
    const p = JSON.parse(raw);
    if (typeof p !== 'object' || p === null) return fallback;
    const dpi = [150, 200, 300].includes(p.dpi) ? p.dpi : 150;
    return { dpi, sharp: !!p.sharp, flattenAll: !!p.flattenAll };
  } catch {
    return fallback;
  }
}

function saveSettings(): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(ui.getExportSettings()));
  } catch {
    /* storage may be unavailable; non-fatal */
  }
}

window.addEventListener('pagehide', () => destroyCurrent());

main();
